// Zod-схемы модуля `learning` — покрытие форматов обучения (coverage).
//
// Coverage отвечает на вопрос «какие форматы уже есть у этого курса / темы /
// материала, какой рекомендован следующим и что заблокировано». Один запрос —
// весь набор каналов темы; UI (карточки каналов, спина ①②③) строится по нему.
import { z } from "zod";
import { isoDateTimeSchema, uuidSchema } from "../_shared/common.js";

// ===== Перечисления =====

/** Скоуп покрытия: курс целиком, одна тема или один материал. */
export const COVERAGE_SCOPES = ["course", "topic", "material"] as const;
export const coverageScopeSchema = z.enum(COVERAGE_SCOPES);
export type CoverageScope = (typeof COVERAGE_SCOPES)[number];

/**
 * Формат обучения. Пять текстовых видов комплекта темы + аудио-канал.
 * Порядок значим — в нём формат-карточки рендерятся в UI темы.
 */
export const COVERAGE_KINDS = [
	"note",
	"glossary",
	"flashcards",
	"quiz",
	"audio-recap",
	"voice-plan",
] as const;
export const coverageKindSchema = z.enum(COVERAGE_KINDS);
export type CoverageKind = (typeof COVERAGE_KINDS)[number];

// ===== DTO =====

/** Покрытие одним форматом в заданном скоупе. */
export const coverageFormatSchema = z.object({
	kind: coverageKindSchema,
	/** Артефакт уже существует (не placeholder). */
	exists: z.boolean(),
	/** Сколько артефактов этого вида в скоупе (карточек / вопросов / терминов). */
	count: z.number().int().nonnegative(),
	/** ID артефакта для перехода; `null` — если ещё не сгенерирован. */
	referenceId: uuidSchema.nullable(),
	lastUsedAt: isoDateTimeSchema.nullable(),
	/** Источник изменился после генерации артефакта → предложить обновить. */
	isStale: z.boolean(),
	/** Оценка времени прохождения формата, минуты. */
	estimatedMinutes: z.number().int().nonnegative(),
	/** Рекомендованный следующий шаг (ровно один формат в наборе). */
	isRecommended: z.boolean(),
	/** Заблокирован спиной темы (например шаг ③ до закрытия шага ②). */
	isLocked: z.boolean(),
});
export type CoverageFormat = z.infer<typeof coverageFormatSchema>;

// ===== Inputs / Outputs =====

export const coverageGetInput = z.object({
	scope: coverageScopeSchema,
	/** `course` → planId, `topic` → subsectionId, `material` → materialId. */
	id: uuidSchema,
});
export const coverageGetOutput = z.object({
	formats: z.array(coverageFormatSchema),
});
